import { state } from '../state';
import { showToast } from '../components/toast';

function esc(str: string): string {
  const el = document.createElement('div');
  el.textContent = str;
  return el.innerHTML;
}

function escAttr(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

function safeHex(v: string | null | undefined): string {
  if (!v) return '';
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v) ? v : '';
}

type ABMethod = 'member' | 'role' | 'matrix' | 'bulk';

function renderABMemberCentric(pane: HTMLElement, serverId: string): void {
  const members = state.serverMembers.filter(m => m.server_id === serverId);
  const roles = state.roles.filter(r => r.server_id === serverId);

  pane.innerHTML = `
    <div class="sss-pane-header">
      <h3 class="sss-pane-title">Method A: Member-Centric</h3>
      <p class="sss-pane-sub">Click a member, then toggle which roles they have. Discord's default approach — best when managing individual members.</p>
    </div>
    <div class="ab-method-desc">
      <div class="ab-pro">Works well for onboarding new members one at a time.</div>
      <div class="ab-con">Tedious when assigning the same role to many members.</div>
    </div>
    <div class="ab-preview">
      ${members.slice(0, 8).map(m => {
        const p = state.people.find(pp => pp.id === m.person_id);
        const name = p?.name || 'Member';
        return `<div class="ab-member-row" data-pid="${escAttr(m.person_id)}">
          <span class="role-member-avatar">${esc(name.charAt(0).toUpperCase())}</span>
          <span class="role-member-name">${esc(name)}</span>
          <div class="ab-role-chips" data-chips="${escAttr(m.person_id)}">
            ${roles.map(r => {
              const hex = safeHex(r.color);
              return `<button type="button" class="ab-chip" data-r="${escAttr(r.id)}" data-p="${escAttr(m.person_id)}"
                style="${hex ? `--chip-color:${hex}` : ''}">${esc(r.name)}</button>`;
            }).join('')}
          </div>
        </div>`;
      }).join('')}
    </div>
  `;

  pane.querySelectorAll<HTMLButtonElement>('.ab-chip').forEach(btn => {
    btn.addEventListener('click', () => {
      btn.classList.toggle('ab-chip-on');
      showToast(`${btn.classList.contains('ab-chip-on') ? 'Assigned' : 'Removed'} (preview only)`, 'ok');
    });
  });
}

function renderABRoleCentric(pane: HTMLElement, serverId: string): void {
  const roles = state.roles.filter(r => r.server_id === serverId);
  const members = state.serverMembers.filter(m => m.server_id === serverId);

  pane.innerHTML = `
    <div class="sss-pane-header">
      <h3 class="sss-pane-title">Method B: Role-Centric</h3>
      <p class="sss-pane-sub">Select a role, then add/remove members. Best for batch-managing a specific role.</p>
    </div>
    <div class="ab-method-desc">
      <div class="ab-pro">Efficient when rolling out a new role to multiple people.</div>
      <div class="ab-con">Harder to see at a glance what roles one person has.</div>
    </div>
    <div class="ab-preview">
      <div class="ab-role-selector">
        ${roles.map((r, i) => {
          const hex = safeHex(r.color);
          return `<button type="button" class="ab-role-tab ${i === 0 ? 'ab-role-tab-on' : ''}" data-rt="${escAttr(r.id)}"
            style="${hex ? `border-color:${hex};--chip-color:${hex}` : ''}">${esc(r.name)}</button>`;
        }).join('')}
      </div>
      <div class="ab-member-list" id="ab-role-members">
        ${members.slice(0, 8).map(m => {
          const p = state.people.find(pp => pp.id === m.person_id);
          const name = p?.name || 'Member';
          return `<div class="ab-member-row">
            <input type="checkbox" class="ab-cb" />
            <span class="role-member-avatar">${esc(name.charAt(0).toUpperCase())}</span>
            <span class="role-member-name">${esc(name)}</span>
          </div>`;
        }).join('')}
      </div>
    </div>
  `;

  pane.querySelectorAll<HTMLButtonElement>('.ab-role-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      pane.querySelectorAll('.ab-role-tab').forEach(b => b.classList.remove('ab-role-tab-on'));
      btn.classList.add('ab-role-tab-on');
    });
  });
}

function renderABMatrix(pane: HTMLElement, serverId: string): void {
  const roles = state.roles.filter(r => r.server_id === serverId);
  const members = state.serverMembers.filter(m => m.server_id === serverId).slice(0, 10);

  const personName = (pid: string): string => {
    const p = state.people.find(pp => pp.id === pid);
    return p?.name || 'Member';
  };

  pane.innerHTML = `
    <div class="sss-pane-header">
      <h3 class="sss-pane-title">Method C: Matrix Grid</h3>
      <p class="sss-pane-sub">Full grid of members vs roles. See everything at once. Best for small-to-medium teams.</p>
    </div>
    <div class="ab-method-desc">
      <div class="ab-pro">Complete visibility — every assignment in one view.</div>
      <div class="ab-con">Gets unwieldy with many roles or members (50+).</div>
    </div>
    <div class="ab-preview" style="overflow-x:auto">
      <table class="ab-matrix">
        <thead>
          <tr>
            <th>Member</th>
            ${roles.map(r => `<th style="color:${safeHex(r.color) || 'var(--fg-muted)'};font-size:11px;writing-mode:vertical-lr;text-orientation:mixed;padding:4px">${esc(r.name)}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${members.map(m => `
            <tr>
              <td style="padding:6px 8px;font-size:13px;white-space:nowrap">${esc(personName(m.person_id))}</td>
              ${roles.map(() => `<td style="text-align:center;padding:4px"><input type="checkbox" class="ab-cb" /></td>`).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;
}

function renderABBulk(pane: HTMLElement, serverId: string): void {
  const roles = state.roles.filter(r => r.server_id === serverId);
  const members = state.serverMembers.filter(m => m.server_id === serverId);

  pane.innerHTML = `
    <div class="sss-pane-header">
      <h3 class="sss-pane-title">Method D: Bulk Assign</h3>
      <p class="sss-pane-sub">Select multiple members, then apply a role to all at once. Best for large teams.</p>
    </div>
    <div class="ab-method-desc">
      <div class="ab-pro">Fastest for assigning one role to many members at once.</div>
      <div class="ab-con">Only handles one role per action.</div>
    </div>
    <div class="ab-preview">
      <div style="display:flex;gap:8px;align-items:center;margin-bottom:12px;flex-wrap:wrap">
        <select class="form-input" style="max-width:200px" id="ab-bulk-role">
          ${roles.map(r => `<option value="${escAttr(r.id)}">${esc(r.name)}</option>`).join('')}
        </select>
        <button class="btn btn-primary btn-sm" id="ab-bulk-apply">Apply to Selected</button>
        <span style="font-size:12px;color:var(--fg-muted)" id="ab-bulk-count">0 selected</span>
      </div>
      <div class="ab-member-list">
        ${members.slice(0, 12).map(m => {
          const p = state.people.find(pp => pp.id === m.person_id);
          const name = p?.name || 'Member';
          return `<div class="ab-member-row">
            <input type="checkbox" class="ab-bulk-cb" />
            <span class="role-member-avatar">${esc(name.charAt(0).toUpperCase())}</span>
            <span class="role-member-name">${esc(name)}</span>
          </div>`;
        }).join('')}
      </div>
    </div>
  `;

  const updateCount = (): void => {
    const count = pane.querySelectorAll<HTMLInputElement>('.ab-bulk-cb:checked').length;
    const el = document.getElementById('ab-bulk-count');
    if (el) el.textContent = `${count} selected`;
  };
  pane.querySelectorAll<HTMLInputElement>('.ab-bulk-cb').forEach(cb => {
    cb.addEventListener('change', updateCount);
  });
  document.getElementById('ab-bulk-apply')?.addEventListener('click', () => {
    const count = pane.querySelectorAll<HTMLInputElement>('.ab-bulk-cb:checked').length;
    showToast(`Would assign role to ${count} members (preview only)`, 'ok');
  });
}

export function renderABTesting(body: HTMLElement): void {
  const sid = state.currentServer;
  if (!sid) {
    body.innerHTML = '<div class="empty-state"><p>No server selected</p></div>';
    return;
  }

  let active: ABMethod = 'member';

  const render = (): void => {
    body.innerHTML = `
      <div class="channel-header">
        <h1>A/B Testing — Role Assignment</h1>
      </div>
      <div class="ab-shell">
        <aside class="ab-sidebar" id="ab-sidebar">
          <div class="us-nav-title">Methods</div>
          <button class="us-nav-item ${active === 'member' ? 'us-nav-active' : ''}" data-ab="member" type="button">Member-centric</button>
          <button class="us-nav-item ${active === 'role' ? 'us-nav-active' : ''}" data-ab="role" type="button">Role-centric</button>
          <button class="us-nav-item ${active === 'matrix' ? 'us-nav-active' : ''}" data-ab="matrix" type="button">Matrix grid</button>
          <button class="us-nav-item ${active === 'bulk' ? 'us-nav-active' : ''}" data-ab="bulk" type="button">Bulk assign</button>
        </aside>
        <section class="ab-content" id="ab-pane"></section>
      </div>
    `;

    const pane = document.getElementById('ab-pane');
    if (!pane) return;

    switch (active) {
      case 'member': renderABMemberCentric(pane, sid); break;
      case 'role': renderABRoleCentric(pane, sid); break;
      case 'matrix': renderABMatrix(pane, sid); break;
      case 'bulk': renderABBulk(pane, sid); break;
    }

    body.querySelectorAll<HTMLButtonElement>('#ab-sidebar [data-ab]').forEach(btn => {
      btn.addEventListener('click', () => {
        active = (btn.dataset['ab'] || 'member') as ABMethod;
        render();
      });
    });
  };

  render();
}
